import type { ReactNode } from "react";
import { cn } from "../lib/cn";

export interface Column<Row> {
  key: string;
  header: ReactNode;
  /** Cell renderer. */
  cell: (row: Row) => ReactNode;
  align?: "left" | "right" | "center";
  /** Optional fixed/relative width, e.g. "30%" or "120px". */
  width?: string;
}

export interface TableProps<Row> {
  columns: Column<Row>[];
  rows: Row[];
  rowKey: (row: Row, index: number) => string | number;
  caption?: string;
  /** Zebra-stripe alternate rows. */
  striped?: boolean;
  onRowClick?: (row: Row) => void;
  className?: string;
}

const alignClass = {
  left: "text-left",
  right: "text-right",
  center: "text-center",
} as const;

/**
 * Data table with a sticky-ready header, optional zebra striping, and
 * keyboard-navigable rows when clickable. Semantic <table> markup for screen
 * readers; the visual chrome is on-brand (ink header, hairline rows).
 */
export function Table<Row>({
  columns,
  rows,
  rowKey,
  caption,
  striped = true,
  onRowClick,
  className,
}: TableProps<Row>) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl bg-surface ring-1 ring-line shadow-sm",
        className,
      )}
    >
      <table className="w-full border-collapse text-sm">
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr className="bg-grad-coral text-white">
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                style={{ width: c.width }}
                className={cn(
                  "px-4 py-3 font-display text-xs font-bold uppercase tracking-kicker",
                  alignClass[c.align ?? "left"],
                )}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={rowKey(row, i)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onKeyDown={
                onRowClick
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onRowClick(row);
                      }
                    }
                  : undefined
              }
              className={cn(
                "border-t border-line-soft transition-colors",
                striped && i % 2 === 1 && "bg-surface-sunken/50",
                onRowClick &&
                  "cursor-pointer hover:bg-coral-soft focus-visible:bg-coral-soft focus-visible:outline-none",
              )}
            >
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={cn("px-4 py-3 text-body", alignClass[c.align ?? "left"])}
                >
                  {c.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
